import React, { useEffect, useState, useRef } from 'react';
import { doc, getDoc, getDocs, updateDoc, increment, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../hooks/useFirebase'; // Ensure this points to your firestore init
import { APP_ID } from '../constants/config';
import { Heart, ArrowLeft, Loader2, Share, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import MetaTags from '../components/MetaTags';
import ThemeToggle from '../components/ThemeToggle';
import Toast from './Toast';

// Robust date parser to handle standard YYYY-MM-DD, ISO, and Notion CSV formats
const safeParseDate = (dateStr) => {
  if (!dateStr) return null;
  const trimmed = dateStr.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return new Date(trimmed + 'T12:00:00');
  }
  const d = new Date(trimmed);
  return isNaN(d.getTime()) ? null : d;
};

const getSortTime = (q) => {
  if (q.date) {
    const d = safeParseDate(q.date);
    if (d) return d.getTime();
  }
  if (q.year) {
    const d = new Date(q.year.toString());
    if (!isNaN(d.getTime())) return d.getTime();
  }
  return 0;
};

const SingleQuote = ({ quoteId, handleNavigate, isDarkMode, toggleDarkMode, theme }) => {
  const [quote, setQuote] = useState(null);
  const [allQuotes, setAllQuotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Swipe & Toast States
  const [toast, setToast] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const minSwipeDistance = 50;

  const cardRef = useRef(null);

  // Helper function to dynamically adjust font size based on quote length
  const getQuoteFontSizeClass = (textLength = 0, forExport = false) => {
    if (textLength > 250) {
      // Smaller font for very long quotes (adjusted for wide typewriter font)
      return forExport ? 'text-sm leading-loose' : 'text-sm md:text-base leading-loose md:leading-loose';
    }
    if (textLength > 150) {
      // Default font for medium quotes (adjusted for wide typewriter font)
      return forExport ? 'text-base leading-loose' : 'text-base md:text-lg leading-loose md:leading-loose';
    }
    // Larger font for short quotes (adjusted for wide typewriter font)
    return forExport ? 'text-lg leading-loose' : 'text-lg md:text-xl leading-loose md:leading-loose';
  };

  const pillStyle = { borderRadius: '9999px' };
  const showToast = (message, type = 'success') => setToast({ message, type });

  useEffect(() => {
    const fetchQuoteAndLogView = async () => {
      setIsLoading(true);
      try {
        // 1. Fetch Quote
        const quoteRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'quotes', quoteId);
        const snapshot = await getDoc(quoteRef);

        if (snapshot.exists()) {
          setQuote({ id: snapshot.id, ...snapshot.data() });

          // 2. Check if user already liked it from localStorage
          const likedQuotes = JSON.parse(localStorage.getItem('likedQuotes') || '[]');
          if (likedQuotes.includes(quoteId)) {
            setIsLiked(true);
          }

          // 3. Log Analytics silently into your existing page_views
          const queryParams = new URLSearchParams(window.location.search);
          const source = queryParams.get('source') || 'direct';

          addDoc(collection(db, 'page_views'), {
            path: `/quote/${quoteId}`,
            source: source,
            timestamp: serverTimestamp(),
            device: /Mobile|Android|iP(hone|od|ad)/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop'
          }).catch(err => console.error("Silent analytics failed", err));
        }
      } catch (error) {
        console.error("Error fetching quote:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (quoteId) {
      fetchQuoteAndLogView();
    }
  }, [quoteId]);

  // Fetch all quotes to enable next/previous navigation
  useEffect(() => {
    const fetchAllQuotes = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'artifacts', APP_ID, 'public', 'data', 'quotes'));
        const quotesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        quotesData.sort((a, b) => getSortTime(b) - getSortTime(a));
        setAllQuotes(quotesData);
      } catch (error) {
        console.error("Failed to fetch all quotes for navigation:", error);
      }
    };
    fetchAllQuotes();
  }, []);

  // Preload html-to-image and background images in the background. In production, downloading this dynamically
  // during the button click can take too long, causing desktop browsers to silently block the download.
  useEffect(() => {
    import('html-to-image').catch(() => { }); // Silently prefetch

    // Silently prefetch background image to ensure instant, high-quality rendering
    const img = new Image();
    img.src = '/Whatsapp%20Background%20Quote.png';
  }, []);

  const handleLike = async () => {
    const wasLiked = isLiked;

    // Optimistic UI update
    setIsLiked(!wasLiked);
    setQuote(prev => ({ ...prev, likes: Math.max(0, (prev.likes || 0) + (wasLiked ? -1 : 1)) }));

    try {
      // Save to local storage to prevent spam
      const likedQuotes = JSON.parse(localStorage.getItem('likedQuotes') || '[]');
      const newLikes = wasLiked ? likedQuotes.filter(id => id !== quoteId) : [...likedQuotes, quoteId];
      localStorage.setItem('likedQuotes', JSON.stringify(newLikes));

      // Update Firebase
      await updateDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'quotes', quoteId), {
        likes: increment(wasLiked ? -1 : 1)
      });
    } catch (error) {
      console.error("Failed to like:", error);
      setIsLiked(wasLiked);
      setQuote(prev => ({ ...prev, likes: Math.max(0, (prev.likes || 0) + (wasLiked ? 1 : -1)) }));
    }
  };

  // Native Mobile Sharing (Web Share API) with WhatsApp Fallback
  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/?quote=${quoteId}`;
    const shareText = `"${quote?.text}" — ${quote?.author || 'Ben Hasan'}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'idiawisdom.',
          text: shareText,
          url: shareUrl
        });
      } catch (error) {
        if (error.name !== 'AbortError') console.error("Error sharing:", error);
      }
    } else {
      window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`, '_blank');
    }
  };

  const handleDownload = async () => {
    if (!cardRef.current) return;
    try {
      setIsDownloading(true);

      // Ensure all custom fonts are completely loaded before calculating layout
      await document.fonts.ready;

      // Wait a brief moment for React to apply the 'rounded-none' classes below
      await new Promise(resolve => setTimeout(resolve, 150));

      // Dynamically import html-to-image so it doesn't bloat the initial page load
      const { toBlob } = await import('html-to-image');

      // html-to-image uses the browser's native SVG rendering engine, 
      // capturing exact CSS (like drop-shadow) perfectly without 1x resolution bugs.
      const blob = await toBlob(cardRef.current, {
        // Render from a fixed 360x640 CSS canvas at 3x for an exact
        // 1080x1920 WhatsApp Status image on every device.
        width: 360,
        height: 640,
        pixelRatio: 3,
        cacheBust: true, // Prevents cached images from failing CORS
        backgroundColor: isDarkMode ? '#1c1917' : '#fafaf9',
        style: {
          transform: 'none', // Prevents composite layer clipping issues
          width: '360px',
          height: '640px',
        }
      });

      if (!blob) throw new Error("Image generation failed");

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeAuthor = (quote.author || 'Ben Hasan').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      link.download = `idiawisdom-${safeAuthor}-${quoteId}.png`;

      // Try to trigger download
      if (typeof link.download !== 'undefined') {
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        window.open(url, '_blank'); // Fallback for older browsers
      }

      // CRITICAL: Delay revoking the URL for 60 seconds.
      // If the user has "Ask where to save" enabled on desktop, 10s is too short and the download fails!
      setTimeout(() => URL.revokeObjectURL(url), 60000); // 60 seconds

      showToast("Quote card saved to device!");
    } catch (error) {
      console.error("Failed to download image:", error);
      showToast("Failed to save image. Please try again.", "error");
    } finally {
      setIsDownloading(false);
    }
  };

  // Calculate adjacent quotes for swipe/click navigation
  const currentIndex = allQuotes.findIndex(q => q.id === quoteId);
  const prevQuoteId = currentIndex > 0 ? allQuotes[currentIndex - 1].id : null; // Prev is newer
  const nextQuoteId = currentIndex !== -1 && currentIndex < allQuotes.length - 1 ? allQuotes[currentIndex + 1].id : null; // Next is older

  // Desktop Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Avoid triggering when user is typing in an input (in case you add a search bar later)
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft' && prevQuoteId) {
        setSwipeDirection('right');
        setTimeout(() => { setSwipeDirection(null); handleNavigate('single-quote', null, prevQuoteId); }, 400);
      } else if (e.key === 'ArrowRight' && nextQuoteId) {
        setSwipeDirection('left');
        setTimeout(() => { setSwipeDirection(null); handleNavigate('single-quote', null, nextQuoteId); }, 400);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [prevQuoteId, nextQuoteId, handleNavigate]);

  // Track both X and Y touch coordinates to distinguish swipe from scroll
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart({ x: e.targetTouches[0].clientX, y: e.targetTouches[0].clientY });
  };

  const onTouchMove = (e) => {
    setTouchEnd({ x: e.targetTouches[0].clientX, y: e.targetTouches[0].clientY });
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distX = touchStart.x - touchEnd.x;
    const distY = touchStart.y - touchEnd.y;
    const absDistX = Math.abs(distX);
    const absDistY = Math.abs(distY);

    // Only navigate on horizontal swipes to avoid conflicting with vertical scrolling
    if (absDistX > absDistY && absDistX > minSwipeDistance) {
      if (distX > 0 && nextQuoteId) {
        // Swiped left → older quote
        setSwipeDirection('left');
        setTimeout(() => { setSwipeDirection(null); handleNavigate('single-quote', null, nextQuoteId); }, 400);
      } else if (distX < 0 && prevQuoteId) {
        // Swiped right → newer quote
        setSwipeDirection('right');
        setTimeout(() => { setSwipeDirection(null); handleNavigate('single-quote', null, prevQuoteId); }, 400);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className={`animate-spin ${theme?.textSubtle || 'text-stone-400'}`} size={40} />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center space-y-6 text-center px-6 animate-in fade-in duration-500">
        <h1 className={`text-2xl font-serif ${theme?.text}`}>Wisdom not found.</h1>
        <button onClick={() => handleNavigate('quotes')} style={pillStyle} className="px-6 py-3 border border-stone-200 dark:border-stone-800 text-xs font-bold uppercase tracking-widest text-stone-500 hover:text-stone-900 dark:hover:text-white transition-colors">
          Return to Archive
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative px-6 animate-in fade-in duration-1000">
      <MetaTags
        title="Quote"
        description={quote.text}
        url={`https://idiawisdom.com/?quote=${quoteId}`}
      />

      {/* Floating Header controls */}
      <div className="absolute top-8 inset-x-6 max-w-md mx-auto flex items-center justify-between z-10">
        <button onClick={() => handleNavigate('quotes')} className={`p-2 opacity-50 hover:opacity-100 transition-opacity ${theme?.text}`}>
          <ArrowLeft size={20} />
        </button>

        {/* Position Indicator */}
        {allQuotes.length > 0 && (
          <div className={`text-[10px] font-bold uppercase tracking-[0.3em] opacity-40 ${theme?.text}`}>
            {currentIndex + 1} / {allQuotes.length}
          </div>
        )}

        <ThemeToggle isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} theme={theme} />
      </div>

      {/* Minimalist Sliding Navigation Arrows - Desktop Only */}
      {prevQuoteId && (
        <button onClick={() => { setSwipeDirection('right'); setTimeout(() => { setSwipeDirection(null); handleNavigate('single-quote', null, prevQuoteId); }, 400); }} className={`hidden md:block fixed left-0 md:left-4 lg:left-12 top-1/2 -translate-y-1/2 p-3 opacity-40 hover:opacity-100 transition-opacity duration-300 z-50 ${theme?.text}`} title="Newer Quote">
          <ChevronLeft className="w-6 h-6 md:w-8 md:h-8 animate-slide-left" strokeWidth={1.5} />
        </button>
      )}
      {nextQuoteId && (
        <button onClick={() => { setSwipeDirection('left'); setTimeout(() => { setSwipeDirection(null); handleNavigate('single-quote', null, nextQuoteId); }, 400); }} className={`hidden md:block fixed right-0 md:right-4 lg:right-12 top-1/2 -translate-y-1/2 p-3 opacity-40 hover:opacity-100 transition-opacity duration-300 z-50 ${theme?.text}`} title="Older Quote">
          <ChevronRight className="w-6 h-6 md:w-8 md:h-8 animate-slide-right" strokeWidth={1.5} />
        </button>
      )}

      {/* Main Quote Focus Area */}
      <div className="flex-1 flex flex-col items-center justify-center max-w-md mx-auto w-full py-24 space-y-4 md:space-y-10">

        {/* Mobile Left Navigation Arrow */}
        {prevQuoteId && (
          <button onClick={() => { setSwipeDirection('right'); setTimeout(() => { setSwipeDirection(null); handleNavigate('single-quote', null, prevQuoteId); }, 400); }} className={`md:hidden self-start p-2 opacity-40 hover:opacity-100 transition-opacity z-40 ${theme?.text}`} title="Newer Quote">
            <ChevronLeft className="w-6 h-6" strokeWidth={1.5} />
          </button>
        )}

        {/* Pinterest-style Quote Card */}
        <div
          ref={cardRef}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          className={`relative ${isDownloading ? 'w-[360px] h-[640px] rounded-none' : 'w-full rounded-[40px]'} overflow-hidden shadow-2xl group transition-all antialiased [text-rendering:geometricPrecision] will-change-transform ${swipeDirection === 'left'
            ? '-translate-x-12 opacity-0 blur-[4px] duration-500 ease-premium'
            : swipeDirection === 'right'
              ? 'translate-x-12 opacity-0 blur-[4px] duration-500 ease-premium'
              : 'animate-cinematic-rise'
            } ${isDarkMode ? 'bg-stone-900' : 'bg-stone-50'}`}
        >
          {/* Background Layer */}
          <div
            className="absolute inset-0 z-0 bg-cover bg-bottom transition-transform duration-1000 group-hover:scale-105"
            style={{ backgroundImage: "url('/Whatsapp%20Background%20Quote.png')" }}
          />

          {/* Night Mode Overlay - Dims the background so white text becomes perfectly readable */}
          <div className={`absolute inset-0 z-10 transition-colors duration-700 pointer-events-none ${isDarkMode ? 'bg-black/60' : 'bg-transparent'}`} />

          {/* Card Content */}
          <div className={`relative z-50 flex flex-col items-center justify-center ${isDownloading ? 'h-[640px] px-8 pt-6 pb-48' : 'min-h-[75vh] md:min-h-[85vh] px-8 pt-6 pb-48 md:px-10 md:pt-8 md:pb-64'} ${theme?.text}`}>

            {/* Text Wrapper */}
            <div className="flex flex-col items-center w-full max-w-xl space-y-8 md:space-y-6 z-10">
              <h1 className={`font-special-elite text-center drop-shadow-lg ${getQuoteFontSizeClass(quote?.text?.length, isDownloading)}`}>
                "{quote.text}"
              </h1>

              <div className="flex flex-col items-center space-y-4 md:space-y-3 pt-2 md:pt-0">
                {/* Elegant Divider */}
                <div className="w-16 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.3em] opacity-90 drop-shadow-md">
                  {quote.author || 'Ben Hasan'}
                </p>
                {(quote.date || quote.year) && (
                  <p className="text-[9px] font-bold uppercase tracking-[0.2em] opacity-60 drop-shadow-md">
                    {quote.date
                      ? (safeParseDate(quote.date) ? safeParseDate(quote.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : quote.date)
                      : quote.year}
                  </p>
                )}
                {quote.tags && (
                  <div className="flex flex-wrap justify-center gap-2 pt-2">
                    {(Array.isArray(quote.tags) ? quote.tags : quote.tags.split(',')).map((tag, i) => (
                      <span key={i} className={`text-[8px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border opacity-80 drop-shadow-md ${isDarkMode ? 'border-stone-100/20 bg-stone-100/10' : 'border-stone-900/10 bg-stone-900/5'}`}>
                        {tag.trim()}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* Mobile Right Navigation Arrow */}
        {nextQuoteId && (
          <button onClick={() => { setSwipeDirection('left'); setTimeout(() => { setSwipeDirection(null); handleNavigate('single-quote', null, nextQuoteId); }, 400); }} className={`md:hidden self-end p-2 opacity-40 hover:opacity-100 transition-opacity z-40 ${theme?.text}`} title="Older Quote">
            <ChevronRight className="w-6 h-6" strokeWidth={1.5} />
          </button>
        )}

      </div>

      {/* Floating Action Dock */}
      <div className={`fixed bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 z-50 flex items-center justify-center gap-6 md:gap-8 px-6 py-3 md:px-8 md:py-4 rounded-full shadow-2xl backdrop-blur-md border transition-all duration-500 ${isDarkMode ? 'bg-black/50 border-white/10' : 'bg-white/80 border-stone-200/50'
        }`}>
        {/* Like */}
        <button onClick={handleLike} className={`group flex items-center gap-2 transition-all duration-300 ${isLiked ? 'text-red-500' : (isDarkMode ? 'text-stone-300 hover:text-red-400' : 'text-stone-600 hover:text-red-500')}`}>
          <Heart size={20} strokeWidth={1.5} fill={isLiked ? 'currentColor' : 'transparent'} className={`${isLiked ? 'animate-in zoom-in duration-300' : 'group-hover:scale-110'} transition-all`} />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">{quote.likes || 0}</span>
        </button>

        <div className={`w-px h-4 ${isDarkMode ? 'bg-white/20' : 'bg-stone-300'}`}></div>

        {/* Share */}
        <button onClick={handleShare} className={`group flex items-center gap-2 transition-all duration-300 ${isDarkMode ? 'text-stone-300 hover:text-emerald-400' : 'text-stone-600 hover:text-emerald-500'}`}>
          <Share size={20} strokeWidth={1.5} className="group-hover:scale-110 transition-transform" />
          <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">Share</span>
        </button>

        <div className={`w-px h-4 ${isDarkMode ? 'bg-white/20' : 'bg-stone-300'}`}></div>

        {/* Save */}
        <button onClick={handleDownload} disabled={isDownloading} className={`group flex items-center gap-2 transition-all duration-300 ${isDarkMode ? 'text-stone-300 hover:text-blue-400' : 'text-stone-600 hover:text-blue-500'}`}>
          {isDownloading ? <Loader2 size={20} strokeWidth={1.5} className="animate-spin" /> : <Download size={20} strokeWidth={1.5} className="group-hover:scale-110 transition-transform" />}
          <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">Save</span>
        </button>
      </div>

      {/* The Ecosystem Trap */}
      <div className="pb-32 md:pb-24 flex justify-center">
        <button
          onClick={() => handleNavigate('quotes')}
          style={pillStyle}
          className="px-8 py-4 bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 text-[10px] font-bold uppercase tracking-[0.2em] hover:opacity-80 transition-all shadow-xl"
        >
          Explore The Archive
        </button>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} isDarkMode={isDarkMode} onClose={() => setToast(null)} />}
    </div>
  );
};

export default SingleQuote;
